import { Component, createRef } from 'preact';
import { createGlobalStyle } from 'styled-components';

import { withBroadcasterChannel } from '@vimeo/use-broadcast-channel';

import {
  BROADCAST_CHANNEL_MESSAGE_PAYLOAD_TYPE,
  BROADCAST_CHANNEL_MESSAGE_TYPE,
  BROADCAST_CHANNEL_NAME,
} from '../constants/embed_settings';

class ErrorContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      invalidPassword: false,
      password: '',
      isSubmitting: false,
    };

    this.newDataUnsubscriberRef = createRef(null);
  }

  handleChange = (event) => {
    this.setState((state) => ({ password: event.target.value }));
  };

  validatePassword = (password) => {
    const url = `/${this.props.type === 'RLE' ? 'live_event' : 'showcase'}/${
      this.props.playlistId
    }/password?password=${window.encodeURIComponent(password)}`;

    return fetch(url, { credentials: 'include' })
      .then((res) => (res.ok ? Promise.resolve(res) : Promise.reject(res)))
      .then((res) => res.json())
      .catch((err) => console.log('err: ', err));
  };

  postPassword = (password) => {
    if (this.props.isChannelActive) {
      this.props.broadcastChannel.postMessage({
        type: BROADCAST_CHANNEL_MESSAGE_TYPE.NEW_DATA,
        payload: {
          sessionId: String(this.props.apiEntityId),
          type: BROADCAST_CHANNEL_MESSAGE_PAYLOAD_TYPE.PASSWORD,
          value: password,
        },
      });
    }
  };

  updateWindow = (password) => {
    if (window !== window.top) {
      /** chromium loses referrer when iframe is refereshed within https://issues.chromium.org/issues/41306076 */
      location.href += `${location.search ? '&' : '?'}password=${window.encodeURIComponent(
        password,
      )}&referrer=${document.referrer}`;
    } else {
      window.location.reload();
    }
  };

  handleSubmit = (event) => {
    event.preventDefault();

    this.validatePassword(this.state.password).then((res) => {
      if (res.password && res.password === true) {
        this.postPassword(this.state.password);

        if (res.redirect_to) {
          window.location.assign(res.redirect_to);
        } else {
          this.updateWindow(this.state.password);
        }
      } else {
        this.setState((state) => ({
          invalidPassword: true,
        }));
      }
    });
  };

  onUnsubscribeFromNewData = () => {
    if (this.newDataUnsubscriberRef.current) {
      this.newDataUnsubscriberRef.current();
    }
  };

  onSubscribeToNewData = () => {
    if (!this.newDataUnsubscriberRef.current) {
      let messageReceived = false;

      this.newDataUnsubscriberRef.current = this.props.subscribe((message) => {
        if (
          message.type !== BROADCAST_CHANNEL_MESSAGE_TYPE.NEW_DATA ||
          String(message?.payload?.sessionId) !== String(this.props.apiEntityId) ||
          messageReceived
        ) {
          return;
        }

        if (message.payload.type === BROADCAST_CHANNEL_MESSAGE_PAYLOAD_TYPE.PASSWORD) {
          messageReceived = true;

          this.validatePassword(message.payload.value)
            .then((res) => {
              if (res.password && res.password === true) {
                this.updateWindow(message.payload.value);
              } else {
                this.setState((state) => ({
                  invalidPassword: true,
                }));
              }
            })
            .finally(() => {
              messageReceived = false;
            });
        }
      });
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.isChannelActive === this.props.isChannelActive || !this.props.isChannelActive) {
      return;
    }

    if (!Boolean(this.state.password)) {
      this.onSubscribeToNewData();

      this.props.sendChannelMessage({
        type: BROADCAST_CHANNEL_MESSAGE_TYPE.ASK_DATA,
        payload: {
          sessionId: String(this.props.apiEntityId),
          type: BROADCAST_CHANNEL_MESSAGE_PAYLOAD_TYPE.PASSWORD,
        },
      });
    } else {
      this.onUnsubscribeFromNewData();
    }
  }

  componentWillUnmount() {
    if (this.props.isChannelActive) {
      this.onUnsubscribeFromNewData();
      this.props.closeChannel();
    }
  }

  /**
   * Launch the authorization redirect window in a popup
   *
   * @param useSSO {boolean} - whether to use the SSO flow (true) or Vimeo login (false)
   */
  handleLogin = (useSSO = true) => {
    const getToken = () =>
      new Promise((resolve) => {
        const messageListener = (event) => {
          if (event.data.type && event.data.type === 'success') {
            window.removeEventListener('message', messageListener);
            resolve();
          }
        };
        window.addEventListener('message', messageListener);
      });

    const loginWindow =
      this.props.type === 'RLE'
        ? window.open(
            `/event/${this.props.playlistId}/login/private?use_sso=${useSSO}`,
            '',
            'popup,width=800,height=600,scrollbars=yes,resizable=yes,titlebar=no,toolbar=no',
          )
        : window.open(
            `/showcase/${this.props.playlistId}/authorize?use_sso=${useSSO}`,
            '',
            'width=800,height=600,menubar=no,scrollbars=no,titlebar=no,toolbar=no',
          );

    getToken().then(() => {
      loginWindow.close();
      location.reload();
    });
  };

  render = (props, state) => {
    return (
      <>
        <GlobalStyles />
        {props.render({
          invalidPassword: state.invalidPassword,
          handleChange: this.handleChange,
          handleSubmit: this.handleSubmit,
          password: state.password,
          handleLogin: this.handleLogin,
        })}
      </>
    );
  };
}

export default withBroadcasterChannel(ErrorContainer, BROADCAST_CHANNEL_NAME);

const GlobalStyles = createGlobalStyle`
  body {
    background: black;
  }
`;
